import { Text, Title } from "@clipboard-health/ui-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Container, Stack, TextareaAutosize, TextField } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

const workedBreakAuthorizationSchema = z.object({
  authorizingEmployee: z
    .object({
      name: z.string().optional(),
      jobTitle: z.string().optional(),
    })
    .optional()
    .superRefine((values, context) => {
      if (values?.jobTitle && !values?.name) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["name"],
          message: "You must also provide the name of the authorizer.",
        });
      }

      if (!values?.jobTitle && values?.name) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["jobTitle"],
          message: "You must also provide the job title of the authorizer.",
        });
      }
    }),
  reason: z
    .string({
      errorMap: () => ({
        message: "Please enter a detailed explanation that is between 60 and 300 characters.",
      }),
    })
    .min(60)
    .max(300),
});
export type WorkedBreakAuthorization = z.infer<typeof workedBreakAuthorizationSchema>;

export interface WorkedBreakAuthorizationPageProps {
  onCancel: () => void;
  onSubmit: (data: WorkedBreakAuthorization) => void;
  facilityName?: string;
}

export function WorkedBreakAuthorizationPage(
  props: WorkedBreakAuthorizationPageProps
): JSX.Element {
  const { onCancel, onSubmit, facilityName } = props;

  const formMethods = useForm<WorkedBreakAuthorization>({
    resolver: zodResolver(workedBreakAuthorizationSchema),
  });

  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors },
  } = formMethods;

  const authorizerName = register("authorizingEmployee.name");
  const authorizerJobTitle = register("authorizingEmployee.jobTitle");
  const reason = register("reason");

  return (
    <Container maxWidth="xl" sx={{ height: "100%" }}>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack direction="column" spacing={2}>
            <Title component="h1">Why didn&apos;t you take a complete break?</Title>
            <Text bold>
              This facility requires a minimum 30-minute unpaid break for professionals working
              longer than six hours.
            </Text>
            <Text>
              If you were unable to take a full 30-minute break due to work demands, we ask that you
              provide a detailed explanation here in order to be compensated for that time. If a
              facility employee directed you to skip break time, please include their name and
              title.
            </Text>
            <Text>
              <strong>
                Payments for worked break time undergo an additional 3-business day approval process
                where the information provided here is reviewed by {facilityName ?? "the facility"}.
              </strong>
              &nbsp; Note this is separate from standard shift payment processing.
            </Text>
            <TextField
              {...authorizerName}
              label="Name of the authorizer"
              error={Boolean(errors.authorizingEmployee?.name?.message)}
              helperText={errors.authorizingEmployee?.name?.message}
              onChange={async (event) => {
                await authorizerName.onChange(event);
                await trigger("authorizingEmployee.name");
                await trigger("authorizingEmployee.jobTitle");
              }}
            />
            <TextField
              {...authorizerJobTitle}
              label="Job title"
              error={Boolean(errors.authorizingEmployee?.jobTitle?.message)}
              helperText={errors.authorizingEmployee?.jobTitle?.message}
              onChange={async (event) => {
                await authorizerJobTitle.onChange(event);
                await trigger("authorizingEmployee.name");
                await trigger("authorizingEmployee.jobTitle");
              }}
            />
            <TextField
              {...reason}
              label="Reason"
              InputProps={{ inputComponent: TextareaAutosize }}
              error={Boolean(errors.reason?.message)}
              helperText={errors.reason?.message}
            />
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={2} padding={2}>
            <Button fullWidth variant="outlined" size="small" onClick={onCancel}>
              Go Back
            </Button>
            <Button fullWidth variant="contained" type="submit" size="small">
              Confirm
            </Button>
          </Stack>
        </form>
      </FormProvider>
    </Container>
  );
}
